import { useEffect, useState } from "react";
import { Package, Inbox, CalendarCheck, IndianRupee, Sparkles, Loader2, TrendingUp, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { renderMarkdown } from "@/lib/markdown";

const COLORS = ["#FF7A00", "#FF3B30", "#002244", "#0a3a63", "#f59e0b"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [leads, setLeads] = useState(null);
  const [range, setRange] = useState("all");

  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "leads") api.get(`/admin/lead-analytics?range=${range}`).then((r) => setLeads(r.data)).catch(() => {}); }, [tab, range]);

  const runAI = async () => {
    setAiLoading(true);
    try { const r = await api.get("/admin/ai/crm"); setInsights(r.data.insights); }
    catch (e) { toast.error(apiError(e, "AI insights failed")); }
    finally { setAiLoading(false); }
  };

  const stats = data ? [
    { label: "Total Tours", value: data.tours, sub: `${data.published_tours} published`, Icon: Package },
    { label: "Enquiries", value: data.total_enquiries, sub: `${data.enquiry_status?.New || 0} new`, Icon: Inbox },
    { label: "Bookings", value: data.total_bookings, sub: "all time", Icon: CalendarCheck },
    { label: "Revenue", value: `₹${Number(data.revenue).toLocaleString("en-IN")}`, sub: "confirmed + paid", Icon: IndianRupee },
  ] : [];

  const enquiryChart = data ? Object.entries(data.enquiry_status).map(([name, value]) => ({ name, value })) : [];
  const destChart = data ? data.top_destinations.map(([name, value]) => ({ name, value })) : [];

  return (
    <AdminLayout title="Dashboard">
      {!data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}</div>
      ) : (
        <>
          <div className="mb-6 flex gap-2">
            {[["overview", "Overview"], ["leads", "Lead Analytics"], ["crm", "AI CRM Insights"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`dash-tab-${k}`} className={`rounded-full px-5 py-2 text-sm font-semibold ${tab === k ? "brand-gradient text-white" : "border border-gray-200 text-navy"}`}>{l}</button>
            ))}
          </div>

          {tab === "crm" ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><Sparkles className="h-5 w-5 text-brandorange" /> AI CRM Insights</h3>
                <button onClick={runAI} disabled={aiLoading} data-testid="ai-crm-btn" className="btn-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-70">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Generate Insights</button>
              </div>
              {insights ? <div className="prose-blog mt-4 text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(insights) }} /> : <p className="mt-4 text-sm text-gray-400">Click "Generate Insights" for AI-powered sales & marketing recommendations based on your enquiries and bookings.</p>}
            </div>
          ) : tab === "leads" ? (
            <div className="space-y-6" data-testid="lead-analytics">
              <div className="flex justify-end">
                <select data-testid="analytics-range" value={range} onChange={(e) => setRange(e.target.value)} className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-navy outline-none">
                  <option value="all">All time</option><option value="this_month">This month</option><option value="last_month">Last month</option><option value="quarter">Quarterly</option><option value="half_year">Half yearly</option><option value="this_year">This year</option><option value="last_year">Last year</option>
                </select>
              </div>
              {!leads ? <div className="h-40 skeleton rounded-2xl" /> : (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display text-3xl font-extrabold text-brandorange">{leads.total_visitors}</p><p className="text-sm text-gray-400">Total Visitors</p></div>
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display text-3xl font-extrabold text-emerald-600">{leads.today_visitors}</p><p className="text-sm text-gray-400">Today's Visitors</p></div>
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display text-3xl font-extrabold text-navy">{leads.total_enquiries}</p><p className="text-sm text-gray-400">Enquiries</p></div>
                  </div>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
                      <h3 className="mb-4 font-display text-lg font-bold text-navy">Enquiries by Source</h3>
                      {Object.keys(leads.by_source).length ? (
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={Object.entries(leads.by_source).map(([name, value]) => ({ name, value }))}>
                            <XAxis dataKey="name" fontSize={12} /><YAxis allowDecimals={false} fontSize={12} /><Tooltip />
                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>{Object.entries(leads.by_source).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-gray-400">No enquiries in this range.</p>}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><h4 className="mb-2 font-display font-bold text-navy">Top Cities</h4>{leads.top_cities.length ? leads.top_cities.map(([n, c]) => <div key={n} className="flex justify-between py-1 text-sm"><span className="truncate text-gray-600">{n}</span><span className="font-bold text-navy">{c}</span></div>) : <p className="text-sm text-gray-400">No data yet</p>}</div>
                      <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><h4 className="mb-2 font-display font-bold text-navy">Top Countries</h4>{leads.top_countries.length ? leads.top_countries.map(([n, c]) => <div key={n} className="flex justify-between py-1 text-sm"><span className="truncate text-gray-600">{n}</span><span className="font-bold text-navy">{c}</span></div>) : <p className="text-sm text-gray-400">No data yet</p>}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
          <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map(({ label, value, sub, Icon }) => (
              <div key={label} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                <div className="flex items-center justify-between"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brandorange/10 text-brandorange"><Icon className="h-5 w-5" /></span></div>
                <p className="mt-4 font-display text-3xl font-extrabold text-navy">{value}</p>
                <p className="text-sm text-gray-400">{label} · {sub}</p>
              </div>
            ))}
          </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-white p-6 card-soft">
                <h3 className="mb-4 font-display text-lg font-bold text-navy">Booking Performance</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ["No. of Tours", data.performance.tours],
                    ["No. of Pax", data.performance.pax],
                    ["Booking Amount", `₹${Number(data.performance.booking_amount).toLocaleString("en-IN")}`],
                    ["Received Amount", `₹${Number(data.performance.received_amount).toLocaleString("en-IN")}`],
                    ["Balance Amount", `₹${Number(data.performance.balance_amount).toLocaleString("en-IN")}`],
                    ["GST Amount", `₹${Number(data.performance.gst_amount).toLocaleString("en-IN")}`],
                    ["Profit", `₹${Number(data.performance.profit).toLocaleString("en-IN")}`],
                    ["Profit %", `${data.performance.profit_percent}%`],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">{k}</p><p className="font-display text-lg font-extrabold text-navy">{v}</p></div>
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display text-3xl font-extrabold text-brandorange">{data.upcoming_count}</p><p className="text-sm text-gray-400">Upcoming Tours (next 45 days)</p></div>
                  <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display text-3xl font-extrabold text-emerald-600">{data.completed_count}</p><p className="text-sm text-gray-400">Completed Tours</p></div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                    <h4 className="mb-2 font-display font-bold text-navy">Top 5 Domestic</h4>
                    {data.top_domestic?.length ? data.top_domestic.map(([n, c]) => <div key={n} className="flex justify-between py-1 text-sm"><span className="truncate text-gray-600">{n}</span><span className="font-bold text-navy">{c}</span></div>) : <p className="text-sm text-gray-400">No data</p>}
                  </div>
                  <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                    <h4 className="mb-2 font-display font-bold text-navy">Top 5 International</h4>
                    {data.top_international?.length ? data.top_international.map(([n, c]) => <div key={n} className="flex justify-between py-1 text-sm"><span className="truncate text-gray-600">{n}</span><span className="font-bold text-navy">{c}</span></div>) : <p className="text-sm text-gray-400">No data</p>}
                  </div>
                </div>
              </div>
            </div>

          {data.upcoming_tours?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-6 card-soft">
              <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-navy"><CalendarClock className="h-5 w-5 text-brandorange" /> Upcoming Departures (next 45 days)</h3>
              <div className="space-y-2">
                {data.upcoming_tours.map((b) => (
                  <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm">
                    <div><span className="font-semibold text-navy">{b.client_name}</span> · {b.tour_title}</div>
                    <div className="flex items-center gap-4"><span className="text-gray-500">{b.travel_date}</span><span className="rounded-full bg-brandred/10 px-2 py-0.5 text-xs font-bold text-brandred">Bal ₹{Number((b.amount || 0) - (b.received_amount || 0)).toLocaleString("en-IN")}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI CRM moved to top tab */}
          </>
          )}
        </>
      )}
    </AdminLayout>
  );
}
